import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Plus, X, Loader2, Search, Eye, Pencil, Trash2, Upload, CheckCircle, Paperclip, RefreshCw,
  Columns, ClipboardList, ChevronDown,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useConfirm } from '../../components/ConfirmDialog';
import { useFeatures } from '../../context/FeatureContext';
import {
  PAGE_WIDE, SearchInput, FilterChips, OverflowMenu, Dialog, DetailDrawer, Field, FormRow, Switch,
  StatusPill, PersonAvatar, EmptyState, ErrorState, TableSkeleton, fmtDate, fmtDayMonth, num, plural,
} from '../../components/ui';

// Tasks: one status filter, then the tasks in the order they need doing — overdue, today, this
// week, later — so the list reads as a to-do list rather than a database table.

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'not_required', label: 'Not required' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'done', label: 'Done' },
  { value: 'invoiced', label: 'Invoiced' },
];
const CLOSED = ['done', 'cancelled', 'invoiced', 'not_required'];

// Seeded and imported tasks carry statuses this page never offered ('Pending', 'Overdue',
// 'Completed'). Read each as the status it means, so the form, the chips and the counts agree. The
// server already treats 'pending' as 'planned' (routes/tasks.js), and overdue is worked out from the
// due date, never stored. Anything unrecognised is kept as it is and offered in the form as-is.
const STATUS_ALIASES = {
  pending: 'planned', open: 'planned', todo: 'planned', to_do: 'planned', not_started: 'planned',
  overdue: 'planned', due_today: 'planned', started: 'in_progress', inprogress: 'in_progress',
  completed: 'done', complete: 'done',
};
const normTaskStatus = (s) => {
  const k = String(s || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!k) return s;
  if (STATUSES.some((x) => x.value === k)) return k;
  return STATUS_ALIASES[k] || s;
};
const MORE_STATUSES = ['cancelled', 'not_required', 'invoiced'];

const DEFAULT_TASK_TYPES = [
  'Support Session', 'Care Planning', 'Assessment', 'Annual Review',
  'Training', 'Administrative', 'Transport', 'Other',
];
const DEFAULT_SUPPORT_CATEGORIES = [
  'Assistance with Daily Life', 'Transport', 'Consumables',
  'Assistance with Social, Economic and Community Participation',
  'Improved Living Arrangements', 'Increased Social and Community Participation',
  'Finding and Keeping a Job', 'Improved Health and Wellbeing', 'Improved Learning',
  'Improved Life Choices', 'Improved Daily Living Skills', 'Improved Relationships',
  'Support Coordination',
];
const DEFAULT_DURATIONS = [
  '15 min', '30 min', '45 min',
  '1 hr', '1 hr 15 min', '1 hr 30 min', '1 hr 45 min',
  '2 hr', '2 hr 30 min', '3 hr', '3 hr 30 min',
  '4 hr', '4 hr 30 min', '5 hr', '5 hr 30 min',
  '6 hr', '6 hr 30 min', '7 hr', '7 hr 30 min', '8 hr',
];

const OPTIONAL_COLUMNS = [
  { key: 'support_category', label: 'Support category' },
  { key: 'duration', label: 'Duration' },
  { key: 'billable', label: 'Billable' },
];
const COLUMNS_KEY = 'tasks.columns';

const ymd = (v) => (v ? String(v).split('T')[0] : '');
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function daysUntil(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd(v));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

// One function decides overdue / due today for the filter counts, the groups and the row labels.
const isOpen = (t) => !CLOSED.includes(t.status);
function dueState(t) {
  const d = daysUntil(t.due_date);
  if (d == null) return 'none';
  if (d < 0) return isOpen(t) ? 'overdue' : 'past';
  if (d === 0) return 'today';
  const toSunday = (7 - new Date().getDay()) % 7;
  return d <= toSunday ? 'week' : 'later';
}
const GROUPS = [
  { id: 'overdue', label: 'Overdue' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'later', label: 'Later' },
  { id: 'past', label: 'Earlier' },
  { id: 'none', label: 'No due date' },
];

// The API replaces every field on save, so a quick status change resends the whole task.
const taskPayload = (task, overrides = {}) => ({
  title: task.title,
  taskType: task.task_type || '',
  clientId: task.client_id || '',
  assignedTo: task.assigned_to || '',
  staffId: task.staff_id || '',
  fundId: task.fund_id || '',
  dueDate: ymd(task.due_date),
  status: task.status,
  description: task.description || '',
  supportCategory: task.support_category || '',
  duration: task.duration || '',
  billable: !!task.billable,
  ...overrides,
});

// ── Search-select (typeahead) ────────────────────────────────────────────────
function SearchSelect({ id, placeholder, value, onChange, options, getLabel, getId }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listId = `${id}-list`;

  useEffect(() => {
    const sel = options.find((o) => getId(o) === value);
    setQuery(sel ? getLabel(sel) : '');
  }, [value, options]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) => getLabel(o).toLowerCase().includes(query.toLowerCase()));
  const select = (opt) => { onChange(getId(opt)); setQuery(getLabel(opt)); setOpen(false); };
  const clear = () => { onChange(''); setQuery(''); setOpen(false); };

  return (
    <div ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          id={id} type="text" role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          value={query} placeholder={placeholder} autoComplete="off"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}
          className="input-field !pl-9 !pr-9"
        />
        {value && (
          <button type="button" onClick={clear} aria-label="Clear"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X size={13} />
          </button>
        )}
      </div>
      {open && (
        <ul id={listId} role="listbox" className="mt-1 bg-white border border-slate-200 rounded-xl shadow-card-deep max-h-52 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-3 py-2.5 text-[13px] text-slate-500">No matches</li>
          ) : filtered.map((opt) => (
            <li key={getId(opt)} role="option" aria-selected={getId(opt) === value}>
              <button type="button" onClick={() => select(opt)}
                className="w-full text-left px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                <PersonAvatar id={getId(opt)} name={getLabel(opt)} size="xs" />
                {getLabel(opt)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Add / edit a task ────────────────────────────────────────────────────────
function TaskDialog({ task, onClose, onSaved }) {
  const isEdit = !!(task && task.id);
  const features = useFeatures();
  const taskTypes = features.task_types && features.task_types.length ? features.task_types : DEFAULT_TASK_TYPES;
  const supportCategories = features.task_support_categories && features.task_support_categories.length ? features.task_support_categories : DEFAULT_SUPPORT_CATEGORIES;
  const durations = features.task_durations && features.task_durations.length ? features.task_durations : DEFAULT_DURATIONS;
  const requireCategory = !!features.require_support_category;

  const [form, setForm] = useState(() => ({
    title:           (task && task.title) || '',
    clientId:        (task && task.client_id) || '',
    supportCategory: (task && task.support_category) || '',
    assignedTo:      (task && task.assigned_to) || '',
    staffId:         (task && task.staff_id) || '',
    fundId:          (task && task.fund_id) || '',
    taskType:        (task && task.task_type) || '',
    status:          (task && task.status) || 'planned',
    dueDate:         ymd(task && task.due_date) || todayYmd(),
    duration:        (task && task.duration) || '',
    description:     (task && task.description) || '',
    billable:        (task && task.billable) || false,
  }));
  const [clients, setClients] = useState([]);
  const [staffList, setStaff] = useState([]);
  const [funds, setFunds] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: '' })); };

  useEffect(() => {
    api.get('/clients').then((r) => setClients(Array.isArray(r.data) ? r.data : ((r.data && r.data.clients) || []))).catch(() => {});
    api.get('/staff').then((r) => setStaff(Array.isArray(r.data) ? r.data : ((r.data && r.data.staff) || []))).catch(() => {});
  }, []);

  // A task's fund belongs to its client, so the fund list follows the client.
  useEffect(() => {
    if (form.clientId) {
      api.get(`/clients/${form.clientId}`).then((r) => {
        const list = (r.data && r.data.funds) || [];
        setFunds(list);
        setForm((f) => (f.fundId && !list.find((x) => x.id === f.fundId) ? { ...f, fundId: '' } : f));
      }).catch(() => {});
    } else {
      setFunds([]);
      setForm((f) => (f.fundId ? { ...f, fundId: '' } : f));
    }
  }, [form.clientId]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const next = {};
    if (!form.title.trim()) next.title = 'Give the task a name.';
    // The organisation's setting: "Users must select a support category when creating or editing a task."
    if (requireCategory && !form.supportCategory) next.supportCategory = 'Your organisation requires a support category on every task.';
    if (next.title || next.supportCategory) { setErrors(next); return; }
    setSaving(true);
    try {
      let saved;
      if (isEdit) { const r = await api.put(`/tasks/${task.id}`, form); saved = r.data; toast.success('Task updated'); }
      else { const r = await api.post('/tasks', form); saved = r.data; toast.success('Task created'); }
      if (file && saved && saved.id) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/tasks/${saved.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not save the task');
    } finally { setSaving(false); }
  };

  const personName = (p) => `${p.first_name} ${p.last_name}`;

  return (
    <Dialog open onClose={onClose} size="lg" title={isEdit ? 'Edit task' : 'New task'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="task-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Create task'}
          </button>
        </>
      )}>
      <form id="task-form" onSubmit={handleSave} className="space-y-4">
        <Field label="Task name" required error={errors.title}>
          {(id) => <input id={id} className="input-field" value={form.title} placeholder="What needs doing" aria-invalid={!!errors.title} onChange={(e) => set('title', e.target.value)} data-autofocus />}
        </Field>
        <FormRow>
          <Field label="Client">
            {(id) => <SearchSelect id={id} placeholder="Search clients" value={form.clientId} onChange={(v) => set('clientId', v)} options={clients} getLabel={personName} getId={(c) => c.id} />}
          </Field>
          <Field label="Staff" hint="The worker the task is about">
            {(id) => <SearchSelect id={id} placeholder="Search staff" value={form.staffId} onChange={(v) => set('staffId', v)} options={staffList} getLabel={personName} getId={(s) => s.id} />}
          </Field>
        </FormRow>
        {/* Who has to do it. The register's "Assigned to" column and its filter read this; the form
            never set it, so the column was always blank. */}
        <Field label="Assigned to" hint="Who is doing the task">
          {(id) => <SearchSelect id={id} placeholder="Search people" value={form.assignedTo} onChange={(v) => set('assignedTo', v)} options={staffList} getLabel={personName} getId={(s) => s.id} />}
        </Field>
        <FormRow>
          <Field label="Support category" required={requireCategory} hint={requireCategory ? undefined : 'Optional'} error={errors.supportCategory}>
            {(id) => (
              <select id={id} className="input-field" value={form.supportCategory} aria-invalid={!!errors.supportCategory} onChange={(e) => set('supportCategory', e.target.value)}>
                <option value="">Choose a support category</option>
                {supportCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                {form.supportCategory && !supportCategories.includes(form.supportCategory) && <option value={form.supportCategory}>{form.supportCategory}</option>}
              </select>
            )}
          </Field>
          <Field label="Fund" hint={form.clientId ? undefined : 'Choose a client first'}>
            {(id) => (
              <select id={id} className="input-field" value={form.fundId} onChange={(e) => set('fundId', e.target.value)} disabled={!form.clientId}>
                <option value="">{form.clientId ? 'Choose a fund' : 'No client chosen'}</option>
                {funds.map((f) => <option key={f.id} value={f.id}>{f.fund_name} ({f.fund_type})</option>)}
              </select>
            )}
          </Field>
          <Field label="Task type">
            {(id) => (
              <select id={id} className="input-field" value={form.taskType} onChange={(e) => set('taskType', e.target.value)}>
                <option value="">Choose a task type</option>
                {taskTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                {form.taskType && !taskTypes.includes(form.taskType) && <option value={form.taskType}>{form.taskType}</option>}
              </select>
            )}
          </Field>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {form.status && !STATUSES.some((s) => s.value === form.status) && <option value={form.status}>{form.status}</option>}
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            )}
          </Field>
          <Field label="Due date">
            {(id) => <input id={id} type="date" className="input-field" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />}
          </Field>
          <Field label="Duration" hint="Billable hours for this task">
            {(id) => (
              <select id={id} className="input-field" value={form.duration} onChange={(e) => set('duration', e.target.value)}>
                <option value="">Choose a duration</option>
                {durations.map((d) => <option key={d} value={d}>{d}</option>)}
                {form.duration && !durations.includes(form.duration) && <option value={form.duration}>{form.duration}</option>}
              </select>
            )}
          </Field>
        </FormRow>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-2.5">
          <span className="text-[13px] font-medium text-slate-800">Billable</span>
          <Switch checked={form.billable} onChange={(v) => set('billable', v)} label="Billable" />
        </div>
        <Field label="Instructions">
          {(id) => <textarea id={id} rows={3} className="input-field resize-y" value={form.description} placeholder="Notes for whoever does the task" onChange={(e) => set('description', e.target.value)} />}
        </Field>
        <div>
          <input ref={fileRef} type="file" accept=".mp4,.jpg,.jpeg,.png,.doc,.docx,.pdf" className="hidden"
            onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)} />
          <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="btn-secondary btn-sm">
            <Upload size={13} /> {isEdit && task.file_name ? 'Replace file' : 'Upload file'}
          </button>
          {file ? (
            <div className="mt-2 flex items-center gap-2 text-[13px] text-slate-700">
              <Paperclip size={13} className="text-slate-400" />
              <span className="truncate">{file.name}</span>
              <button type="button" aria-label="Remove file" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"><X size={13} /></button>
            </div>
          ) : (
            <p className="mt-1.5 text-[12px] text-slate-500">A document, image or video about the task (MP4, JPG, PNG, DOC or PDF).</p>
          )}
          {isEdit && task.file_name && !file && (
            <div className="mt-2 flex items-center gap-2 text-[13px]">
              <Paperclip size={13} className="text-slate-400" />
              <a href={`${process.env.REACT_APP_API_URL || ''}${task.file_url}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                target="_blank" rel="noreferrer" className="text-primary-600 hover:underline truncate">{task.file_name}</a>
            </div>
          )}
        </div>
      </form>
    </Dialog>
  );
}

// ── Task detail ──────────────────────────────────────────────────────────────
function TaskDrawer({ task, onClose, onEdit, onDelete, onMarkDone }) {
  const due = dueState(task);
  return (
    <DetailDrawer open onClose={onClose} width="md" title={task.title}
      statusNode={(
        <span className="flex items-center gap-1.5 flex-wrap">
          <StatusPill status={task.status} />
          {due === 'overdue' && <span className="badge-red">Overdue</span>}
          {task.billable && <span className="badge-gray">Billable</span>}
        </span>
      )}
      meta={[
        { label: 'Client', value: task.client_name },
        { label: 'Staff', value: task.staff_name },
        { label: 'Assigned to', value: task.assignee_name || 'Unassigned' },
        { label: 'Task type', value: task.task_type },
        { label: 'Due', value: task.due_date ? fmtDate(ymd(task.due_date)) : '' },
        { label: 'Duration', value: task.duration },
        { label: 'Fund', value: task.fund_name },
        { label: 'Created', value: task.created_at ? fmtDate(task.created_at) : '' },
      ]}
      footer={(
        <>
          <button type="button" onClick={onDelete} className="btn-ghost text-rose-600 hover:bg-rose-50 hover:text-rose-700 mr-auto"><Trash2 size={15} /> Delete</button>
          {!['done', 'invoiced'].includes(task.status) && (
            <button type="button" onClick={onMarkDone} className="btn-secondary"><CheckCircle size={15} /> Mark done</button>
          )}
          <button type="button" onClick={onEdit} className="btn-primary"><Pencil size={15} /> Edit task</button>
        </>
      )}>
      <div className="space-y-4">
        {task.support_category && (
          <div>
            <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1">Support category</h4>
            <p className="text-[13px] text-slate-800">{task.support_category}</p>
          </div>
        )}
        {task.description && (
          <div>
            <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1">Instructions</h4>
            <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{task.description}</p>
          </div>
        )}
        {task.file_name && (
          <div className="flex items-center gap-2 text-[13px]">
            <Paperclip size={13} className="text-slate-400" />
            <a href={`${process.env.REACT_APP_API_URL || ''}${task.file_url}`} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{task.file_name}</a>
          </div>
        )}
        {!task.support_category && !task.description && !task.file_name && (
          <p className="text-[13px] text-slate-500">No instructions or files on this task.</p>
        )}
      </div>
    </DetailDrawer>
  );
}

// ── Column chooser ───────────────────────────────────────────────────────────
function ColumnMenu({ shown, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true" className="btn-secondary">
        <Columns size={15} /> Columns <ChevronDown size={13} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-30 w-56 bg-white border border-slate-200 rounded-xl shadow-card-deep p-1.5">
          <p className="px-2.5 pt-1 pb-1.5 text-[12px] text-slate-500">Show extra columns</p>
          {OPTIONAL_COLUMNS.map((c) => (
            <label key={c.key} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 cursor-pointer text-[13px] text-slate-700">
              <input type="checkbox" checked={shown.includes(c.key)} onChange={() => onToggle(c.key)} className="rounded" />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const TH = 'py-2.5 px-3 text-left text-[12px] font-semibold text-slate-500 whitespace-nowrap';

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Tasks() {
  const confirm = useConfirm();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(null); // null = all · a status · 'due_today' · 'overdue'
  const [dialogTask, setDialogTask] = useState(undefined); // undefined = closed · null = new · task
  const [viewTask, setViewTask] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [extraCols, setExtraCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(COLUMNS_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
  });

  useEffect(() => { const t = setTimeout(() => setSearch(searchDraft.trim()), 300); return () => clearTimeout(t); }, [searchDraft]);

  const loadTasks = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    api.get(`/tasks?${p}`)
      .then((r) => setTasks((Array.isArray(r.data) ? r.data : []).map((t) => ({ ...t, status: normTaskStatus(t.status) }))))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const toggleCol = (key) => setExtraCols((cols) => {
    const next = cols.includes(key) ? cols.filter((k) => k !== key) : [...cols, key];
    try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(next)); } catch { /* per-browser convenience only */ }
    return next;
  });

  const closeDialog = useCallback(() => setDialogTask(undefined), []);
  const closeView = useCallback(() => setViewTask(null), []);

  const handleDelete = async (task) => {
    const ok = await confirm(`"${task.title}" is removed from the task list.`, { title: 'Delete this task?', confirmLabel: 'Delete task', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Task deleted');
      setViewTask(null);
      loadTasks();
    } catch { toast.error('Could not delete the task'); }
  };

  const markDone = async (task) => {
    setBusyId(task.id);
    try {
      await api.put(`/tasks/${task.id}`, taskPayload(task, { status: 'done' }));
      toast.success('Marked done');
      setViewTask((v) => (v && v.id === task.id ? null : v));
      loadTasks();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not update the task');
    } finally { setBusyId(null); }
  };

  // Counts and rows come from the same functions. Only real statuses are counted by status: a task
  // stored with status 'overdue' used to add to the Overdue chip as well as being overdue by date,
  // so the chips read 'All 6' beside 'Overdue 7'.
  const counts = useMemo(() => {
    const c = { all: tasks.length, due_today: 0, overdue: 0 };
    STATUSES.forEach((s) => { c[s.value] = 0; });
    tasks.forEach((t) => {
      if (STATUSES.some((s) => s.value === t.status)) c[t.status] += 1;
      const d = dueState(t);
      if (d === 'overdue') c.overdue += 1;
      if (d === 'today' && isOpen(t)) c.due_today += 1;
    });
    return c;
  }, [tasks]);

  const visible = useMemo(() => tasks.filter((t) => {
    if (!filter) return true;
    if (filter === 'overdue') return dueState(t) === 'overdue';
    if (filter === 'due_today') return dueState(t) === 'today' && isOpen(t);
    return t.status === filter;
  }), [tasks, filter]);

  const groups = useMemo(() => GROUPS.map((g) => ({
    ...g,
    rows: visible.filter((t) => dueState(t) === g.id).sort((a, b) => {
      const da = ymd(a.due_date); const db = ymd(b.due_date);
      if (da !== db) return g.id === 'past' ? (da < db ? 1 : -1) : (da < db ? -1 : 1);
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    }),
  })).filter((g) => g.rows.length > 0), [visible]);

  const chipOptions = [
    { value: null, label: 'All', count: counts.all },
    { value: 'planned', label: 'Planned', count: counts.planned },
    { value: 'in_progress', label: 'In progress', count: counts.in_progress },
    { value: 'due_today', label: 'Due today', count: counts.due_today },
    { value: 'overdue', label: 'Overdue', count: counts.overdue, tone: counts.overdue > 0 ? 'rose' : undefined },
    { value: 'done', label: 'Done', count: counts.done },
    ...(MORE_STATUSES.includes(filter) ? [{ value: filter, label: STATUSES.find((s) => s.value === filter).label, count: counts[filter] }] : []),
  ];

  const show = (key) => extraCols.includes(key);
  const colCount = 7 + OPTIONAL_COLUMNS.filter((c) => show(c.key)).length;
  const filtersOn = !!filter || !!searchDraft.trim();

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center min-w-0">
          <SearchInput value={searchDraft} onChange={setSearchDraft} placeholder="Search tasks" className="w-full sm:w-72" />
          <span className="text-[13px] text-slate-500 tabular-nums whitespace-nowrap" aria-live="polite">
            {loading ? 'Loading…' : filter ? `${num(visible.length)} of ${plural(tasks.length, 'task')}` : plural(tasks.length, 'task')}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={loadTasks} className="btn-secondary !px-2.5" aria-label="Refresh tasks"><RefreshCw size={15} /></button>
          <ColumnMenu shown={extraCols} onToggle={toggleCol} />
          <button type="button" onClick={() => setDialogTask(null)} className="btn-primary"><Plus size={16} /> New task</button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <FilterChips multi={false} ariaLabel="Filter tasks" value={filter} onChange={setFilter} options={chipOptions} />
        <OverflowMenu label="More statuses" size={15} className="h-7 w-7 !p-0 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white"
          items={MORE_STATUSES.map((s) => ({
            label: STATUSES.find((x) => x.value === s).label, hint: num(counts[s] || 0), onClick: () => setFilter(s),
          }))} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <TableSkeleton rows={7} cols={6} />
          : loadError ? <ErrorState message="We couldn't load tasks. Check your connection, then try again." onRetry={loadTasks} />
          : groups.length === 0 ? (
            filtersOn
              ? <EmptyState icon={ClipboardList} title="No tasks match these filters" action={<button type="button" className="btn-secondary btn-sm" onClick={() => { setFilter(null); setSearchDraft(''); }}>Clear filters</button>} />
              : <EmptyState icon={ClipboardList} title="No tasks yet" hint="Add follow-ups, reviews and admin work so nothing is forgotten."
                  action={<button type="button" className="btn-primary btn-sm" onClick={() => setDialogTask(null)}><Plus size={14} /> New task</button>} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                <caption className="sr-only">Tasks, grouped by when they are due</caption>
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className="w-10 pl-4 pr-1 py-2.5"><span className="sr-only">Done</span></th>
                    <th scope="col" className={TH}>Task</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={TH}>Due</th>
                    <th scope="col" className={TH}>Client</th>
                    <th scope="col" className={TH}>Assigned to</th>
                    {show('support_category') && <th scope="col" className={TH}>Support category</th>}
                    {show('duration') && <th scope="col" className={TH}>Duration</th>}
                    {show('billable') && <th scope="col" className={TH}>Billable</th>}
                    <th scope="col" className={clsx(TH, 'text-right')}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                {groups.map((g) => (
                  <tbody key={g.id}>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th scope="rowgroup" colSpan={colCount} className="px-4 py-1.5 text-left">
                        <span className={clsx('text-[12px] font-semibold', g.id === 'overdue' ? 'text-rose-700' : 'text-slate-600')}>{g.label}</span>
                        <span className="ml-2 text-[12px] text-slate-500 tabular-nums">{num(g.rows.length)}</span>
                      </th>
                    </tr>
                    {g.rows.map((task) => {
                      const due = dueState(task);
                      const closed = ['done', 'invoiced'].includes(task.status);
                      return (
                        <tr key={task.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors duration-150">
                          <td className="pl-4 pr-1 py-2.5 align-top">
                            {busyId === task.id ? <Loader2 size={15} className="animate-spin text-slate-400 mt-0.5" /> : (
                              <input type="checkbox" checked={closed} disabled={closed}
                                onChange={() => markDone(task)} className="mt-0.5 h-4 w-4 rounded cursor-pointer disabled:cursor-default"
                                aria-label={closed ? `${task.title} is ${task.status === 'invoiced' ? 'invoiced' : 'done'}` : `Mark ${task.title} done`} />
                            )}
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            <button type="button" onClick={() => setViewTask(task)}
                              className={clsx('block max-w-[22rem] truncate text-left font-medium hover:text-primary-700 hover:underline underline-offset-2',
                                closed ? 'text-slate-500' : 'text-slate-900')}>
                              {task.title}
                            </button>
                            <div className="text-[12px] text-slate-500 truncate max-w-[22rem]">
                              {[task.task_type, task.billable ? 'Billable' : null].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 align-top"><StatusPill status={task.status} /></td>
                          <td className="px-3 py-2.5 align-top whitespace-nowrap">
                            {task.due_date ? (
                              <div className="leading-tight">
                                <span className={clsx('tabular-nums', due === 'overdue' ? 'font-medium text-rose-600' : 'text-slate-800')}>
                                  {fmtDate(ymd(task.due_date))}
                                </span>
                                {due === 'overdue' && <span className="text-[12px] font-medium text-rose-600"> (overdue)</span>}
                                {due === 'today' && isOpen(task) && <span className="text-[12px] font-medium text-amber-700"> (today)</span>}
                              </div>
                            ) : <span className="text-slate-500">—</span>}
                            {task.created_at && <div className="text-[12px] text-slate-500">Created {fmtDayMonth(task.created_at)}</div>}
                          </td>
                          <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">{task.client_name || '—'}</td>
                          <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">{task.assignee_name || '—'}</td>
                          {show('support_category') && <td className="px-3 py-2.5 align-top text-slate-700"><span className="block max-w-[14rem] truncate" title={task.support_category || undefined}>{task.support_category || '—'}</span></td>}
                          {show('duration') && <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">{task.duration || '—'}</td>}
                          {show('billable') && <td className="px-3 py-2.5 align-top text-slate-700">{task.billable ? 'Yes' : 'No'}</td>}
                          <td className="px-3 py-2 align-top text-right">
                            <OverflowMenu label={`Actions for ${task.title}`} size={16} className="p-2" items={[
                              { label: 'View details', icon: Eye, onClick: () => setViewTask(task) },
                              { label: 'Edit task', icon: Pencil, onClick: () => setDialogTask(task) },
                              !closed && { label: 'Mark done', icon: CheckCircle, onClick: () => markDone(task) },
                              { divider: true },
                              { label: 'Delete task', icon: Trash2, danger: true, onClick: () => handleDelete(task) },
                            ]} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          )}
      </div>

      {dialogTask !== undefined && <TaskDialog task={dialogTask} onClose={closeDialog} onSaved={loadTasks} />}
      {viewTask && (
        <TaskDrawer task={viewTask} onClose={closeView}
          onEdit={() => { setDialogTask(viewTask); setViewTask(null); }}
          onDelete={() => handleDelete(viewTask)}
          onMarkDone={() => markDone(viewTask)} />
      )}
    </div>
  );
}
